import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, ArrowUpCircle, Film, Tv, Music, PlayCircle, Sparkles } from 'lucide-react';

const SORA = "'Sora', system-ui, sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";

const VotesList = ({ votes = [], compact = false }) => {
  const [hoveredVote, setHoveredVote] = useState(null);

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'movie': return Film;
      case 'tv': return Tv;
      case 'music': return Music;
      case 'anime': return PlayCircle;
      default: return Film;
    }
  };

  const getVoteConfig = (voteType) => {
    switch (voteType) {
      case 'reaction_request': return { label: 'Request', color: '#8B5CF6', icon: Sparkles };
      case 'upvote': return { label: 'Upvote', color: '#22C55E', icon: ThumbsUp };
      case 'downvote': return { label: 'Downvote', color: '#EF4444', icon: ThumbsDown };
      default: return { label: 'Vote', color: '#6B7280', icon: ThumbsUp };
    }
  };

  return (
    <div style={{ fontFamily: MANROPE }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: compact ? 'center' : 'flex-start', gap: '8px', marginBottom: '14px' }}>
        <ArrowUpCircle size={16} color="#6ee7b7" />
        <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '700', fontFamily: SORA }}>Votes</span>
        <span style={{ color: '#6f6b7d', fontSize: '12px' }}>({votes.length})</span>
      </div>

      {/* Votes List */}
      {votes.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {votes.slice(0, 8).map((vote, index) => {
            const voteConfig = getVoteConfig(vote.voteType);
            const ContentIcon = getContentIcon(vote.contentType);
            const VoteIcon = voteConfig.icon;
            const isHovered = hoveredVote === index;

            return (
              <div
                key={index}
                style={{
                  display: 'flex', gap: '10px', padding: '10px 12px',
                  background: isHovered ? `${voteConfig.color}10` : 'transparent',
                  borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s ease',
                }}
                onMouseEnter={() => setHoveredVote(index)}
                onMouseLeave={() => setHoveredVote(null)}
              >
                {/* Content Icon */}
                <div style={{
                  width: '36px', height: '36px', borderRadius: '8px',
                  background: '#181722', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <ContentIcon size={18} color="#8f8b9c" />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    color: '#FFFFFF', fontSize: '12px', fontWeight: '500', margin: 0, marginBottom: '3px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {vote.contentTitle || vote.contentId}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: '#6f6b7d', fontSize: '10px', textTransform: 'capitalize' }}>
                      {vote.contentType}
                    </span>
                    <span style={{ color: '#6f6b7d' }}>•</span>
                    <span style={{ color: '#6f6b7d', fontSize: '10px' }}>{formatDate(vote.votedAt)}</span>
                  </div>
                </div>

                {/* Vote Badge */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '4px 10px', background: `${voteConfig.color}20`,
                  borderRadius: '8px', flexShrink: 0, alignSelf: 'center',
                }}>
                  <VoteIcon size={12} color={voteConfig.color} />
                  <span style={{ color: voteConfig.color, fontSize: '10px', fontWeight: '600' }}>
                    {voteConfig.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '30px 10px' }}>
          <ThumbsUp size={24} color="#6f6b7d" style={{ opacity: 0.5, marginBottom: '8px' }} />
          <p style={{ color: '#6f6b7d', fontSize: '12px', margin: 0 }}>No votes yet</p>
        </div>
      )}
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return 'recently';
  const diffDays = Math.floor((Date.now() - new Date(dateString)) / 86400000);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
};

export default VotesList;
